import { useState, useRef, useMemo, useEffect, MouseEventHandler, useCallback } from "react";
import clsx from "clsx";
// Helpers
import { applyFormatter } from "@/Washouts/helpers/tables";
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
// Materials
import { Collapse, Divider, Grid } from "@mui/material";
import { GridCellProps, GridColDef, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
// Icons
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
// Custom components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Types and constants
import { Issue } from "@/Common/types/tables";
import { StringMap } from "@/Common/interfaces/types";
import { Product, FooterModelsToShow, DealsTableProps } from "@/Washouts/types/sales";
import { GridStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import {
	COLUMN_RESIZE_FACTOR,
	DEFAULT_COLLAPSED_SIZE,
	DEFAULT_TABLE_MAX_ROWS,
	HEADER_CLASS_NAME,
	ID_GRID_COLDEF,
} from "@/Washouts/constants";
import { DATE_FORMAT } from "@/Constants/date";

const REFERENCE = "PendingDeals";
const TITLE = "Pending Deals";
const FINANCE_NAME_WIDTH = 150;
const DEAL_NAME_WIDTH = 160;

const buildColumns = (items: Product[] = [], key = "", issues: Issue[] = []) => {
	return items.map((element: Product) => {
		const colDef: GridColDef = {
			field: `${key}_${element.product}`,
			headerName: element.product,
			type: "number",
			width: 110,
			resizable: false,
			align: "right",
			filterable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => (
				<TypographyCell
					render="absolute"
					reference={REFERENCE}
					params={params}
					issues={issues}
					hints={{ isFunded: false, isMoney: true }}
				/>
			),
			valueGetter: (_value: number, row: GridValidRowModel) => {
				const product = row?.[key]?.filter((item: Product) => item.product === element.product);
				return product?.[0]?.value || 0;
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
		};
		return colDef;
	});
};

function getInitialColDef(issues: Issue[]): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		{
			field: "saleDate",
			headerName: "Sale Date",
			type: "date",
			width: 90,
			flex: 1,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateDashShortYear),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }}>Total</TypographyCell>;
				}
				return <TypographyCell reference={REFERENCE} params={params} issues={issues} />;
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealNumber",
			headerName: "Deal Number",
			width: 85,
			flex: 1,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealStatus",
			headerName: "Deal Status",
			width: 90,
			flex: 1,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (row) => {
				if (!row.value) {
					return null;
				}
				return (
					<TypographyCell>
						<CircleRoundedIcon className={`deal-status-${row.value} !text-[12px] -mt-[3px] mr-[5px]`} />
						{row.value}
					</TypographyCell>
				);
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "financeManagerName",
			headerName: "Finance Manager Name",
			minWidth: FINANCE_NAME_WIDTH,
			maxWidth: COLUMN_RESIZE_FACTOR * FINANCE_NAME_WIDTH,
			flex: 1.5,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { noWrap: true }, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} hints={{ noWrap: true }} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealManagerName",
			headerName: "Deal Manager Name",
			minWidth: DEAL_NAME_WIDTH,
			maxWidth: COLUMN_RESIZE_FACTOR * DEAL_NAME_WIDTH,
			flex: 1.5,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock Number",
			width: 75,
			flex: 1,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleType",
			headerName: "Sale Type",
			width: 60,
			flex: 0.5,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "units",
			headerName: "Units",
			type: "number",
			width: 55,
			resizable: false,
			align: "right",
			headerClassName: `${HEADER_CLASS_NAME} header-end`,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			valueFormatter: (params) => applyFormatter(params, "account"),
			renderCell: (params) => (
				<TypographyCell render="account" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
	];
}

function DealsTable(props: Readonly<DealsTableProps>) {
	const emptyTitle = props?.emptyTitle ?? TITLE;
	const apiRef = useGridApiRef();
	const [showColumns, setShowColumns] = useState(false);
	const [collapsed, setCollapsed] = useState<boolean>(() => props.collapsed ?? true);

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget?.parentElement?.dataset.id ?? "", // _id
			columnReference: event.currentTarget?.dataset.field ?? "", // field
			tableReference: props.issuesReference ?? "",
			color: activeIssue.current?.color ?? "",
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const updateCurrentIssue = (color: string, currentIssues: Issue[]) => {
		let isPresent = false;
		for (const issue of currentIssues) {
			if (
				issue &&
				issue.rowReference === activeIssue?.current?.rowReference &&
				issue.columnReference === activeIssue?.current?.columnReference
			) {
				issue.color = color;
				isPresent = true;
				break;
			}
		}

		if (!isPresent) {
			currentIssues.push({
				tableReference: props?.issuesReference,
				rowReference: activeIssue?.current?.rowReference ?? "",
				columnReference: activeIssue?.current?.columnReference ?? "",
				color,
			});
		}
		return currentIssues;
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...issues];
		if (color) {
			currentIssues = updateCurrentIssue(color, currentIssues);
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue?.rowReference === activeIssue?.current?.rowReference &&
					issue?.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter(Boolean);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	// Clear filters once (mount)
	const clearFilters = useCallback(() => {
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and", quickFilterLogicOperator: "and", quickFilterValues: [] },
					visibleRows: null,
					visibleRowsLookup: {},
				};
				apiRef.current.setState(
					(prevState) =>
						({
							...prevState,
							filter: { ...filter, ...cleanFilter },
						} as GridStatePremium)
				);
			}
		} catch (error) {
			errorLogger(getError(error));
		}
	}, [apiRef]);

	useEffect(() => {
		clearFilters();
	}, [clearFilters]);

	/**
	 * Derive all dynamic table structures from data + issues.
	 */
	const { initialColumns, initialVisibilityModel, footerModels, columnsToExpand } = useMemo(() => {
		const visibilityModel: StringMap<boolean> = { _id: false };
		const footer: FooterModelsToShow = {
			units: "sum",
			frontGrossProfit: "sum",
			backGrossProfit: "sum",
			frontCommission: "sum",
			backCommission: "sum",
			grossCommission: "sum",
		};
		const expand: string[] = [];
		const columns = getInitialColDef(issues);

		try {
			let frontMax = 0;
			let backMax = 0;
			let frontKey = -1;
			let backKey = -1;

			const data = props.data ?? [];

			data.forEach((element, key) => {
				if (element.frontProducts?.length > frontMax) {
					frontMax = element.frontProducts.length;
					frontKey = key;
				}

				if (element.backProducts?.length > backMax) {
					backMax = element.backProducts.length;
					backKey = key;
				}
			});

			// Front Products
			if (frontKey !== -1) {
				const frontProducts = data[frontKey]?.frontProducts ?? [];
				columns.push(...buildColumns(frontProducts, "frontProducts", issues));

				frontProducts.forEach((p) => {
					const key = `frontProducts_${p.product}`;
					visibilityModel[key] = true;
					footer[key] = "sum";
					expand.push(key);
				});
			}

			// Front Gross Profit
			columns.push({
				field: "frontGrossProfit",
				headerName: "Front Gross Profit",
				type: "number",
				width: 110,
				flex: 1,
				resizable: false,
				align: "right",
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
				renderCell: (params) => (
					<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
				),
				renderHeader: (params) => <TypographyHeader params={params} />,
			});

			// Back Products
			if (backKey !== -1) {
				const backProducts = data[backKey]?.backProducts ?? [];
				columns.push(...buildColumns(backProducts, "backProducts", issues));
				backProducts.forEach((p) => {
					const key = `backProducts_${p.product}`;
					visibilityModel[key] = true;
					footer[key] = "sum";
					expand.push(key);
				});
			}

			// Back Gross Profit
			columns.push({
				field: "backGrossProfit",
				headerName: "Back Gross Profit",
				type: "number",
				width: 110,
				flex: 1,
				resizable: false,
				align: "right",
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
				renderCell: (params) => (
					<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
				),
				renderHeader: (params) => <TypographyHeader params={params} />,
			});
		} catch (error) {
			errorLogger(getError(error));
		}

		return {
			initialColumns: columns,
			initialVisibilityModel: visibilityModel,
			footerModels: footer,
			columnsToExpand: expand,
		};
	}, [props.data, issues]);

	// Local visibility model state for user expand/collapse interactions
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<StringMap<boolean>>(initialVisibilityModel);
	useEffect(() => {
		setColumnVisibilityModel(initialVisibilityModel);
	}, [props.data]);

	// Clean up on unmount
	useEffect(() => {
		return () => {
			activeIssue.current = null;
		};
	}, []);

	if (!footerModels) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
		return undefined;
	}

	return (
		<>
			<Collapse in={!collapsed} collapsedSize={DEFAULT_COLLAPSED_SIZE} timeout={100}>
				<DataSmartTable
					maxRows={DEFAULT_TABLE_MAX_ROWS + 1}
					sx={{ width: "1400px" }}
					apiRef={apiRef}
					columns={initialColumns}
					rows={props?.data ?? []}
					hideFooter
					disableColumnMenu
					disableRowSelectionOnClick
					disableMultipleRowSelection
					checkboxSelection={false}
					columnVisibilityModel={columnVisibilityModel}
					initialState={{
						aggregation: {
							model: footerModels,
						},
						sorting: {
							sortModel: [{ field: "saleDate", sort: "asc" }],
						},
					}}
					slots={{
						toolbar: CommonToolbar,
						noRowsOverlay: NoRowsOverlay,
					}}
					slotProps={{
						toolbar: {
							titleExpandible: {
								collapsed,
								hide: false,
								collapsible: true,
								title: props.title ?? TITLE,
								titleClasses: "page-title",
								sxContainer: { padding: "20px 32px" },
								onClick: (value: boolean) => setCollapsed(value),
							},
							filter: {
								hide: false,
							},
							search: {
								data: props.data ?? [],
								hide: false,
								disabled: false,
							},
							expand: {
								setColumnVisibilityModel,
								hide: false,
								expanded: showColumns,
								columnsCollapsible: columnsToExpand,
								onExpandAction: (expanded: boolean) => {
									setShowColumns(expanded);
								},
							},
						},
						noRowsOverlay: {
							title: `No ${emptyTitle} Data`,
						},
						cell: getCell(),
					}}
					customProps={{
						tableClassName: {
							"[&_>.MuiDataGrid-main]:mx-[30px] [&_>.MuiDataGrid-main]:mb-[15px]": true,
							"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[15px]": true,
						},
					}}
					{...props.tableProps}
				/>
				{props.addIssues && (
					<AddCellIssues
						open={openAddIssues}
						anchorEl={anchorAddIssues}
						onCloseAction={() => {
							setOpenAddIssues(false);
							setAnchorAddIssues(null);
						}}
						onChangeAction={handleChangeIssues}
					/>
				)}
			</Collapse>
			<Collapse in={props.divider} collapsedSize={0} timeout={100}>
				{props.sxSectionDivider ? (
					<Divider sx={props.sxSectionDivider}></Divider>
				) : (
					<Divider
						className={clsx({
							"!mx-[24px] !border-b !border-[#CCC]": true,
							"pt-0": collapsed,
							"pt-[5px] !mt-[10px]": !collapsed,
						})}
					></Divider>
				)}
			</Collapse>
		</>
	);
}

export default function PendingDealsTable(props: Readonly<DealsTableProps>) {
	if (props.loading) {
		return (
			<>
				<Grid className="py-[20px] px-[32px]">
					<TableLoader smallTitle toolbar />
				</Grid>
				{props.divider &&
					(props.sxSectionDivider ? (
						<Divider sx={props.sxSectionDivider ?? {}}></Divider>
					) : (
						<Divider className="!mx-[24px] !border-b !border-[#CCC]"></Divider>
					))}
			</>
		);
	}

	return <>{props.dataPresent && <DealsTable {...props} />}</>;
}
